import { useEffect, useRef, useState } from "react";
import { Button, Card, Checkbox, ConfigProvider, Flex, Popover, Tabs, Typography } from "antd";
import {
  AlertOutlined, ClockCircleOutlined, DollarOutlined, HolderOutlined, MoreOutlined,
  PercentageOutlined, RetweetOutlined, ShopOutlined, TeamOutlined, ThunderboltOutlined, UserAddOutlined,
} from "@ant-design/icons";
import { useGetIdentity } from "@refinedev/core";
import {
  DndContext, closestCenter, PointerSensor, KeyboardSensor, useSensor, useSensors, type DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove, SortableContext, useSortable, rectSortingStrategy, sortableKeyboardCoordinates,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

import { KpiChips } from "../kpi-chips";
import { PedidosChips } from "../pedidos-chips";
import { ComparativaChips } from "../comparativa-chips";
import { TasaConversionChip } from "../tasa-conversion-chip";
import { ValorPipelineChip } from "../valor-pipeline-chip";
import { PresupuestosRiesgoChip } from "../presupuestos-riesgo-chip";
import { TiempoRespuestaChip } from "../tiempo-respuesta-chip";
import { NuevosContactosChip } from "../nuevos-contactos-chip";
import { TiempoPromedioCierreChip } from "../tiempo-cierre-chip";
import { PerformanceOperadoresChip } from "../performance-operadores-chip";
import { DashboardFilters } from "../dashboard-filters";
import type { OperatorAffiliationSelection } from "@/components";
import { operadoresSeleccionados, marcarOperadoresVaciados, type DashboardPeriod } from "../../utils";
import type { User } from "@/types/schema/schema.types";
import { useSelectedStores } from "@/config/context/SelectedItemsContext";
import { useDashboardMetrics } from "../../hooks/use-dashboard-metrics";
import { WIDGETS_SIN_DATOS, type DashboardResponse, type WidgetId } from "../../api/dashboard.api";
import type { UseDashboardConfig } from "../../hooks/use-dashboard-config";

const { Text } = Typography;

// ── Constants ─────────────────────────────────────────────────────────────────

const NUM_COLS      = 3;
const NUM_ROWS      = 2;
const PAGE_SIZE     = NUM_COLS * NUM_ROWS; // 6 metrics per page (3 cols × 2 rows)
const DEFAULT_H  = 240;   // px — initial row height
const MIN_FR     = 0.25;  // minimum column fraction (prevents collapse)
const MIN_ROW_H  = 160;   // px — minimum row height (80px chart + 24px legend + 38px header + 18px padding)
const GRID_GAP   = 16;    // px — gap between cards (also the handle zone)
const HANDLE_W   = 20;    // px — card drag-to-reorder handle width
const CARD_HEAD  = 38;    // px — Ant Design small card header height

// ── Metric registry ───────────────────────────────────────────────────────────

type MetricId =
  | "presupuestos" | "pedidos" | "comparativa"
  | "tasa-conversion" | "valor-pipeline" | "presupuestos-riesgo"
  | "tiempo-cierre" | "nuevos-contactos" | "tiempo-respuesta"
  | "performance-operadores";

const METRIC_LABELS: Record<MetricId, string> = {
  "presupuestos":            "Presupuestos por etapa",
  "pedidos":                 "Pedidos por estado",
  "comparativa":             "Comparativa de período",
  "tasa-conversion":         "Tasa de conversión",
  "valor-pipeline":          "Valor del pipeline",
  "presupuestos-riesgo":     "Presupuestos en riesgo",
  "tiempo-cierre":           "Tiempo de resolución de la conversación",
  "nuevos-contactos":        "Nuevos contactos",
  "tiempo-respuesta":        "Tiempo de respuesta",
  "performance-operadores":  "Performance del operador",
};

const ALL_METRIC_IDS: MetricId[] = [
  "presupuestos", "pedidos", "comparativa",
  "tasa-conversion", "valor-pipeline", "presupuestos-riesgo",
  "tiempo-cierre", "nuevos-contactos", "tiempo-respuesta",
  "performance-operadores",
];

const DEFAULT_ACTIVE: MetricId[] = ["presupuestos", "pedidos", "comparativa"];

const METRIC_ICONS: Record<MetricId, React.ReactNode> = {
  "presupuestos":            null,
  "pedidos":                 null,
  "comparativa":             <RetweetOutlined     style={{ fontSize: 14, color: "rgba(0,0,0,0.65)" }} />,
  "tasa-conversion":         <PercentageOutlined  style={{ fontSize: 14, color: "rgba(0,0,0,0.65)" }} />,
  "valor-pipeline":          <DollarOutlined      style={{ fontSize: 14, color: "rgba(0,0,0,0.65)" }} />,
  "presupuestos-riesgo":     <AlertOutlined       style={{ fontSize: 14, color: "rgba(0,0,0,0.65)" }} />,
  "tiempo-cierre":           <ClockCircleOutlined style={{ fontSize: 14, color: "rgba(0,0,0,0.65)" }} />,
  "nuevos-contactos":        <UserAddOutlined     style={{ fontSize: 14, color: "rgba(0,0,0,0.65)" }} />,
  "tiempo-respuesta":        <ThunderboltOutlined style={{ fontSize: 14, color: "rgba(0,0,0,0.65)" }} />,
  "performance-operadores":  <TeamOutlined        style={{ fontSize: 14, color: "rgba(0,0,0,0.65)" }} />,
};

// ── Stub card ─────────────────────────────────────────────────────────────────

const StubCard = ({ id, label, height }: { id: MetricId; label: string; height: number }) => (
  <Card
    size="small"
    style={{ height, border: "1px dashed #d9d9d9", background: "rgba(0,0,0,0.01)" }}
    styles={{ body: { height: `calc(100% - ${CARD_HEAD}px)`, display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: 4 } }}
    title={<Flex align="center" gap={8}>{METRIC_ICONS[id]}<span style={{ color: "rgba(0,0,0,0.65)", fontWeight: 600 }}>{label}</span></Flex>}
  >
    <Text style={{ fontSize: 11, color: "rgba(0,0,0,0.25)" }}>Próximamente</Text>
  </Card>
);

// ── Widget renderer ───────────────────────────────────────────────────────────

type WidgetProps = {
  period: DashboardPeriod;
  operatorId: string | null;
  stores: any[];
  height: number;
  selectedOperatorIds: string[];
  /** El userId del operador logueado, para que el widget sepa cuál fila es "yo". */
  myOperatorId: number | null;
  /** La respuesta del endpoint, compartida por todos los widgets de la página. */
  metrics: DashboardResponse | null;
  estado: "cargando" | "listo" | "error";
  error: string | null;
  recargar: () => void;
};

/** Lo que cada widget recibe de la respuesta común, más su estado de carga. */
const datosDe = (p: WidgetProps, id: WidgetId) => ({
  data: p.metrics?.widgets?.[id] as any,
  estado: p.estado,
  error: p.error,
  nuncaCalculado: p.metrics?.nuncaCalculado,
  recargar: p.recargar,
});

function renderMetric(id: MetricId, props: WidgetProps) {
  switch (id) {
    case "presupuestos":        return <KpiChips stores={props.stores} period={props.period} operatorId={props.operatorId} totalHeight={props.height} {...datosDe(props, "presupuestos")} />;
    case "pedidos":             return <PedidosChips period={props.period} height={props.height} {...datosDe(props, "pedidos")} />;
    case "comparativa":         return <ComparativaChips period={props.period} height={props.height} {...datosDe(props, "comparativa")} />;
    case "tasa-conversion":     return <TasaConversionChip height={props.height} period={props.period} {...datosDe(props, "tasa-conversion")} />;
    case "valor-pipeline":      return <ValorPipelineChip height={props.height} period={props.period} {...datosDe(props, "valor-pipeline")} />;
    case "presupuestos-riesgo": return <PresupuestosRiesgoChip height={props.height} period={props.period} {...datosDe(props, "presupuestos-riesgo")} />;
    // Sin fuente de datos todavía: conversation-service no expone métricas. Ver el change.
    case "tiempo-respuesta":    return <TiempoRespuestaChip height={props.height} period={props.period} />;
    case "nuevos-contactos":    return <NuevosContactosChip height={props.height} period={props.period} {...datosDe(props, "nuevos-contactos")} />;
    case "tiempo-cierre":            return <TiempoPromedioCierreChip height={props.height} period={props.period} {...datosDe(props, "tiempo-cierre")} />;
    case "performance-operadores":   return <PerformanceOperadoresChip height={props.height} period={props.period} selectedOperatorIds={props.selectedOperatorIds} myOperatorId={props.myOperatorId} {...datosDe(props, "performance-operadores")} />;
    default:                         return <StubCard id={id} label={METRIC_LABELS[id]} height={props.height} />;
  }
}

// ── Sortable card (reorder via drag handle) ───────────────────────────────────

function SortableCard({ id, children }: { id: string; children: React.ReactNode }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  return (
    <div ref={setNodeRef} style={{ transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.4 : 1, position: "relative", height: "100%" }}>
      <div
        {...attributes} {...listeners}
        style={{ position: "absolute", top: 0, left: 0, width: HANDLE_W, height: CARD_HEAD, zIndex: 10, cursor: isDragging ? "grabbing" : "grab", display: "flex", alignItems: "center", justifyContent: "center", touchAction: "none", borderRadius: "8px 0 0 0" }}
      >
        <HolderOutlined style={{ fontSize: 11, color: "rgba(0,0,0,0.65)" }} />
      </div>
      {children}
    </div>
  );
}

// ── Resize handle ──────────────────────────────────────────────────────────────

function ResizeHandle({ dir, onMouseDown }: { dir: "col" | "row"; onMouseDown: (e: React.MouseEvent) => void }) {
  const [hover, setHover] = useState(false);
  const isCol = dir === "col";
  const color = hover ? "rgba(0,0,0,0.35)" : "rgba(0,0,0,0.15)";
  return (
    <div
      onMouseDown={onMouseDown}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{ cursor: isCol ? "col-resize" : "row-resize", display: "flex", alignItems: "center", justifyContent: "center", width: "100%", height: "100%", userSelect: "none" }}
    >
      {/* Vertical gutter → 2 vertical lines (||) · Horizontal gutter → 2 horizontal lines */}
      <div style={{ display: "flex", flexDirection: isCol ? "row" : "column", gap: 3 }}>
        {[0, 1].map((i) => (
          <div key={i} style={{
            width:        isCol ? 1.5 : 16,
            height:       isCol ? 16  : 1.5,
            background:   color,
            borderRadius: 2,
            transition:   "background 0.15s",
          }} />
        ))}
      </div>
    </div>
  );
}

// ── Resizable grid ────────────────────────────────────────────────────────────

/**
 * El tamaño de cada widget vive en la config del operador, no en el estado del componente.
 *
 * Antes era `useState` acá adentro: el operador arrastraba, quedaba lindo, y se perdía con el F5.
 * `onGridChange` va con debounce del lado del hook — un arrastre emite un evento por píxel.
 */
function ResizableGrid({
  items,
  widgetProps,
  colFr,
  rowH,
  onGridChange,
}: {
  items: MetricId[];
  widgetProps: Omit<WidgetProps, "height">;
  colFr: number[];
  rowH: number[];
  onGridChange: (g: { colFr: number[]; rowH: number[] }) => void;
}) {
  const containerRef = useRef<HTMLDivElement>(null);

  const setColFr = (fn: (prev: number[]) => number[]) =>
    onGridChange({ colFr: fn(colFr), rowH });
  const setRowH = (fn: (prev: number[]) => number[]) =>
    onGridChange({ colFr, rowH: fn(rowH) });

  const dragRef = useRef<{
    type: "col" | "row";
    idx: number;
    startPos: number;
    startA: number;
    startB: number;
    totalFr: number;
    containerW: number;
  } | null>(null);

  const beginResize = (type: "col" | "row", idx: number, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const totalFr    = colFr.reduce((s, f) => s + f, 0);
    const containerW = containerRef.current?.getBoundingClientRect().width ?? 1;
    dragRef.current = {
      type, idx,
      startPos:   type === "col" ? e.clientX : e.clientY,
      startA:     type === "col" ? colFr[idx]   : rowH[idx],
      startB:     type === "col" ? colFr[idx+1] : rowH[idx+1],
      totalFr, containerW,
    };

    const onMove = (ev: MouseEvent) => {
      const d = dragRef.current;
      if (!d) return;
      if (d.type === "col") {
        const deltaFr = ((ev.clientX - d.startPos) / d.containerW) * d.totalFr;
        const a = Math.max(MIN_FR, d.startA + deltaFr);
        const b = Math.max(MIN_FR, d.startB - deltaFr);
        setColFr((prev) => { const next = [...prev]; next[d.idx] = a; next[d.idx + 1] = b; return next; });
      } else {
        const delta = ev.clientY - d.startPos;
        const a = Math.max(MIN_ROW_H, d.startA + delta);
        const b = Math.max(MIN_ROW_H, d.startB - delta);
        setRowH((prev) => { const next = [...prev]; next[d.idx] = a; next[d.idx + 1] = b; return next; });
      }
    };
    const onUp = () => {
      dragRef.current = null;
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
    };
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
  };

  const colTemplate = colFr.map((f) => `${f}fr`).join(" ");
  const rowTemplate = rowH.map((h) => `${h}px`).join(" ");

  return (
    <>
      <style>{`.mp-grid .ant-card-head { padding-left: ${HANDLE_W + 8}px !important; }`}</style>

      <div
        ref={containerRef}
        className="mp-grid"
        style={{ display: "grid", gridTemplateColumns: colTemplate, gridTemplateRows: rowTemplate, gap: GRID_GAP, width: "100%", overflow: "visible" }}
      >
        {Array.from({ length: PAGE_SIZE }, (_, i) => {
          const id  = items[i] ?? null;
          const col = i % NUM_COLS;
          const row = Math.floor(i / NUM_COLS);
          const h   = rowH[row] ?? DEFAULT_H;
          return (
            <div key={id ?? `empty-${i}`} style={{ position: "relative", overflow: "visible" }}>
              {id ? (
                <SortableCard id={id}>
                  {renderMetric(id, { ...widgetProps, height: h })}
                </SortableCard>
              ) : (
                <div style={{ height: h }} />
              )}

              {col < NUM_COLS - 1 && id !== null && items[i + 1] !== undefined && (
                <div style={{ position: "absolute", left: "100%", top: "50%", transform: "translateY(-50%)", zIndex: 20, width: GRID_GAP, height: 40 }}>
                  <ResizeHandle dir="col" onMouseDown={(e) => beginResize("col", col, e)} />
                </div>
              )}

              {row < NUM_ROWS - 1 && id !== null && items[i + NUM_COLS] !== undefined && (
                <div style={{ position: "absolute", top: "100%", left: "50%", transform: "translateX(-50%)", zIndex: 20, width: 40, height: GRID_GAP }}>
                  <ResizeHandle dir="row" onMouseDown={(e) => beginResize("row", row, e)} />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </>
  );
}

// ── Customization popover ─────────────────────────────────────────────────────

/**
 * Lo que el menú de personalización ofrece.
 *
 * `tiempo-respuesta` **no está**: sus datos viven en `conversation-service`, que todavía no expone
 * métricas. Ofrecerlo dejaría activar un widget que sólo puede mostrar números inventados, y eso es
 * peor que no ofrecerlo. Vuelve cuando exista la fuente.
 */
const METRIC_SECTIONS: { label: string; ids: MetricId[] }[] = [
  { label: "Presupuestos",   ids: ["presupuestos", "tasa-conversion", "valor-pipeline", "presupuestos-riesgo", "tiempo-cierre"] },
  { label: "Pedidos",        ids: ["pedidos", "comparativa"] },
  { label: "Contactos",      ids: ["nuevos-contactos"] },
  { label: "Operadores",     ids: ["performance-operadores"] },
];

function CustomizationContent({ activeIds, onToggle }: { activeIds: Set<MetricId>; onToggle: (id: MetricId) => void }) {
  return (
    <div style={{ width: 240 }}>
      {METRIC_SECTIONS.map((section, si) => (
        <div key={section.label}>
          {si > 0 && <div style={{ height: 1, background: "#f0f0f0", margin: "8px 0 6px" }} />}
          <Text style={{ fontSize: 11, fontWeight: 600, color: "rgba(0,0,0,0.4)", display: "block", marginBottom: 2, padding: "0 4px" }}>
            {section.label}
          </Text>
          {section.ids.map((id) => (
            <div key={id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "5px 4px", borderRadius: 4, cursor: "pointer" }} onClick={() => onToggle(id)}>
              <Checkbox checked={activeIds.has(id)} onClick={(e) => e.stopPropagation()} onChange={() => onToggle(id)} />
              <Text style={{ fontSize: 13, flex: 1, lineHeight: "20px", userSelect: "none" }}>{METRIC_LABELS[id]}</Text>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

// ── MetricsPanel ─────────────────────────────────────────────────────────────

/**
 * La config llega por props y no se pide acá: la home entera comparte una sola —el filtro de stores
 * vive afuera de este componente y escribe en la misma—. Dos `useDashboardConfig` serían dos GET y
 * dos estados que se pisan.
 */
export const MetricsPanel = ({
  stores,
  configuracion,
  onFrescura,
}: {
  stores: any[];
  configuracion: UseDashboardConfig;
  /**
   * Informa de cuándo son los datos, para que el marcador se dibuje al lado de la fecha del día
   * (QA #273.5). Se avisa hacia arriba en vez de subir el fetch: el panel es el único que sabe
   * qué widgets están en pantalla, y esa es la clave de la request.
   */
  onFrescura?: (f: { calculadoHasta: string | null; nuncaCalculado: boolean } | null) => void;
}) => {
  const { data: user } = useGetIdentity<User>();
  const u = user as any;

  const { config, cargada: prefsLoaded, actualizar } = configuracion;

  const [customOpen, setCustomOpen] = useState(false);
  const [page, setPage]             = useState(0);

  // Se derivan de la config, con los defaults del panel como red por si el servidor no respondió.
  const widgetsCfg = config?.widgets ?? [];
  const order: MetricId[] = widgetsCfg.length
    ? ([...widgetsCfg].sort((a, b) => a.order - b.order).map((w) => w.id) as MetricId[])
        .filter((id) => ALL_METRIC_IDS.includes(id))
        .concat(ALL_METRIC_IDS.filter((id) => !widgetsCfg.some((w) => w.id === id)))
    : ALL_METRIC_IDS;
  const activeIds = new Set<MetricId>(
    widgetsCfg.length
      ? (widgetsCfg.filter((w) => w.visible).map((w) => w.id) as MetricId[])
      : DEFAULT_ACTIVE,
  );
  const period = (config?.filters?.period ?? "day") as DashboardPeriod;
  const myId = u?.id ? String(u.id) : null;
  // Sin toggle "Mi panel" (xcons-docs#318): la propia tarjeta es un operador más, pre-tildada por
  // default cuando no hay nada guardado todavía y no se vació a propósito.
  const operatorsGuardados = config?.filters?.operators ?? [];
  const selectedOperatorIds = operadoresSeleccionados(operatorsGuardados, myId);

  /**
   * Reescribe la lista de widgets a partir de un orden y un conjunto de visibles.
   *
   * Se filtran los que el endpoint no conoce —hoy `tiempo-respuesta`, que sigue en el panel pero no
   * tiene fuente de datos—. El `PUT` los rechaza con 400, y como el guardado falla en silencio, la
   * consecuencia era que **nada se guardaba nunca**.
   */
  /** Los que el panel muestra pero el endpoint todavía no sabe contestar. */
  const sinDatos = new Set<string>(WIDGETS_SIN_DATOS);

  const guardarWidgets = (nuevoOrden: MetricId[], visibles: Set<MetricId>) =>
    actualizar({
      widgets: nuevoOrden
        .filter((id) => !sinDatos.has(id))
        .map((id, i) => ({
          id,
          visible: visibles.has(id),
          order: i,
          page: 0,
        })),
    });

  const setPeriod = (p: DashboardPeriod) =>
    actualizar({ filters: { ...(config?.filters ?? { stores: [], operators: [] }), period: p } as any });

  const setOpIds = (ids: string[]) => {
    const nums = ids.map(Number).filter(Number.isFinite);
    // Vaciar todo (incluida la propia tarjeta) es una elección — no debe volver a pre-tildarse
    // sola la próxima vez. Elegir algo de nuevo deja de necesitar esa marca.
    if (myId) marcarOperadoresVaciados(myId, nums.length === 0);
    actualizar({
      filters: { ...(config?.filters ?? { period: "day", stores: [] }), operators: nums } as any,
    });
  };

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleToggle = (id: MetricId) => {
    const next = new Set(activeIds);
    next.has(id) ? next.delete(id) : next.add(id);
    guardarWidgets(order, next);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      const next = arrayMove(
        order,
        order.indexOf(active.id as MetricId),
        order.indexOf(over.id as MetricId),
      );
      guardarWidgets(next, activeIds);
    }
  };

  /**
   * Los operadores que van al endpoint. Sin sentinel "mine": la propia tarjeta viaja como el
   * userId que es, igual que cualquier otro operador elegido (xcons-docs#318). Los contactos no
   * tienen dimensión de operador y el endpoint lo ignora para ellos declarándolo
   * (`ignoraFiltroDeOperador`); "Performance" decide el modo del lado de la API.
   */
  const operadoresAPedir = selectedOperatorIds
    .map((id) => parseInt(id, 10))
    .filter((n) => Number.isFinite(n) && n > 0);
  const effectiveOperatorId: string | null = selectedOperatorIds[0] ?? null;

  const { selectedStores } = useSelectedStores();

  // Los badges tildados en el side panel de filtros, por sesión (xcons-docs#309). La config sólo
  // guarda userIds; esto es para que reabrir el panel lo muestre como quedó.
  const [operadoresSeleccion, setOperadoresSeleccion] = useState<OperatorAffiliationSelection[]>([]);

  // Con un solo store accesible, el filtro de arriba lo preselecciona solo — acá nunca hay nada que
  // "elegir". El placeholder es sólo para cuando hay varios stores y el operador todavía no cortó por
  // ninguno: sin esto, "sin filtro" se leía como "todos", y el panel pedía el alcance completo.
  const sinStoreSeleccionado = stores.length > 1 && selectedStores.length === 0;

  const activeOrdered = order.filter((id) => activeIds.has(id));
  const totalPages    = Math.max(1, Math.ceil(activeOrdered.length / PAGE_SIZE));
  const safePage      = Math.min(page, totalPages - 1);
  const pageItems     = activeOrdered.slice(safePage * PAGE_SIZE, (safePage + 1) * PAGE_SIZE);

  // Se piden SÓLO los widgets que están en pantalla ahora mismo. Los apagados y los de otras páginas
  // no generan trabajo del lado del servidor, que es el punto del endpoint batch.
  const widgetsAPedir = pageItems.filter((id) => !sinDatos.has(id)) as WidgetId[];

  const { data: metrics, estado, error, recargar } = useDashboardMetrics(
    prefsLoaded && widgetsAPedir.length && !sinStoreSeleccionado
      ? {
          widgets: widgetsAPedir,
          period,
          stores: selectedStores.map((s) => s.code),
          operators: operadoresAPedir,
        }
      : null,
  );

  useEffect(() => {
    onFrescura?.(
      metrics
        ? { calculadoHasta: metrics.calculadoHasta, nuncaCalculado: metrics.nuncaCalculado }
        : null,
    );
  }, [metrics?.calculadoHasta, metrics?.nuncaCalculado]);

  // Montar widgets antes de saber qué tiene tildado el operador dispara fetch que se tiran a la
  // basura: el panel arrancaba con DEFAULT_ACTIVE y recién después leía sus preferencias.
  if (!prefsLoaded) {
    return (
      <div style={{ border: "1px solid #e8e8e8", borderRadius: 12, padding: "12px 16px 16px", background: "#fff", minHeight: DEFAULT_H }} />
    );
  }

  return (
    <div style={{ border: "1px solid #e8e8e8", borderRadius: 12, padding: "12px 16px 16px", background: "#fff" }}>
      {/* Header */}
      <Flex align="center" justify="space-between" style={{ marginBottom: 12 }}>
        <ConfigProvider theme={{ components: { Tabs: { horizontalItemPaddingSM: "2px 0", itemColor: "rgba(0,0,0,0.35)", itemSelectedColor: "rgba(0,0,0,0.5)", itemHoverColor: "rgba(0,0,0,0.5)", inkBarColor: "rgba(0,0,0,0.35)" } } }}>
          <Tabs
            activeKey={period}
            onChange={(key) => setPeriod(key as DashboardPeriod)}
            size="small" style={{ margin: 0 }} tabBarStyle={{ marginBottom: 0 }}
            items={([
              { key: "day", label: "Hoy" }, { key: "week", label: "Esta semana" },
              { key: "prev_week", label: "Semana anterior" },
              { key: "month", label: "Mes en curso" }, { key: "prev_month", label: "Mes anterior" },
            ] as { key: DashboardPeriod; label: string }[]).map(({ key, label }) => ({
              key,
              label: <span style={{ fontWeight: period === key ? 600 : 400, fontSize: 12 }}>{label}</span>,
            }))}
          />
        </ConfigProvider>
        <Flex align="center" gap={10}>
          <DashboardFilters
            value={selectedOperatorIds}
            onChange={setOpIds}
            storeCodes={selectedStores.map((st) => st.code)}
            disabled={selectedStores.length === 0}
            seleccion={operadoresSeleccion}
            onSeleccion={setOperadoresSeleccion}
          />
          <Popover open={customOpen} onOpenChange={setCustomOpen} placement="bottomRight" trigger="click"
            content={<CustomizationContent activeIds={activeIds} onToggle={handleToggle} />}
          >
            <Button type="text" size="small" icon={<MoreOutlined />} style={{ color: customOpen ? "rgba(0,0,0,0.65)" : "rgba(0,0,0,0.35)" }} />
          </Popover>
        </Flex>
      </Flex>

      {/* Resizable grid */}
      {sinStoreSeleccionado ? (
        <Flex vertical align="center" justify="center" gap={12} style={{ height: DEFAULT_H }}>
          <ShopOutlined style={{ fontSize: 56, color: "rgba(0,0,0,0.12)" }} />
          <Text type="secondary" style={{ fontSize: 13 }}>
            Seleccioná un Store para ver el panel de control y sus métricas.
          </Text>
        </Flex>
      ) : activeOrdered.length === 0 ? (
        <Flex align="center" justify="center" style={{ height: DEFAULT_H }}>
          <Text type="secondary" style={{ fontSize: 13 }}>Sin métricas activas — usá el menú ⋮ para activar alguna.</Text>
        </Flex>
      ) : (
        <>
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={pageItems} strategy={rectSortingStrategy}>
              <ResizableGrid
                items={pageItems}
                colFr={config?.grid?.colFr ?? [1, 1, 1]}
                rowH={config?.grid?.rowH ?? [DEFAULT_H, DEFAULT_H]}
                onGridChange={(grid) => actualizar({ grid })}
                widgetProps={{ period, operatorId: effectiveOperatorId, stores, selectedOperatorIds, myOperatorId: myId ? Number(myId) : null, metrics, estado, error, recargar }}
              />
            </SortableContext>
          </DndContext>

          {totalPages > 1 && (
            <Flex justify="center" gap={6} style={{ marginTop: 12 }}>
              {Array.from({ length: totalPages }, (_, i) => (
                <div
                  key={i}
                  onClick={() => setPage(i)}
                  style={{
                    width: i === safePage ? 18 : 6,
                    height: 6,
                    borderRadius: 3,
                    background: i === safePage ? "rgba(0,0,0,0.45)" : "rgba(0,0,0,0.15)",
                    cursor: "pointer",
                    transition: "all 0.25s ease",
                  }}
                />
              ))}
            </Flex>
          )}
        </>
      )}
    </div>
  );
};
